import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Calendar, ExternalLink, Youtube } from 'lucide-react';
import PageTemplate from '../components/Common/PageTemplate';

const videoData = [
    {
        id: 1,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/2024%20mezuniyet/IMG_0266.jfif",
        videoUrl: "https://www.youtube.com/watch?v=oxsozHdvxUs",
        title: "Fakültemiz 2024 Yılı Mezuniyet Töreni Videosu",
        date: "10.07.2024",
        type: "Mezuniyet"
    },
    {
        id: 2,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/2023%20Mezuniyet/IMG_0269.JPG",
        videoUrl: "https://www.youtube.com/watch?v=O9plIo_ngvI",
        title: "Fakültemiz 2023 Yılı Mezuniyet Töreni Videosu",
        date: "20.07.2023",
        type: "Mezuniyet"
    },
    {
        id: 3,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/mezuniyet%20foto.jpg",
        videoUrl: "https://www.youtube.com/watch?v=AkRJx520WYA",
        title: "Hacettepe Üniversitesi Fen Fakültesi Mezuniyet Töreni 2022",
        date: "19.07.2022",
        type: "Mezuniyet"
    },
    {
        id: 4,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/mezuniyet%20foto.jpg",
        videoUrl: "https://www.youtube.com/watch?v=R4KJ3it2g2w",
        title: "Hacettepe Üniversitesi Fen Fakültesi Mezuniyet Töreni 2021",
        date: "19.07.2022",
        type: "Mezuniyet"
    },
    {
        id: 5,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/tntm.jpeg",
        videoUrl: "https://www.youtube.com/watch?v=zeJ_jVDUTuY",
        title: "Hacettepe Üniversitesi Fen Fakültesi Tanıtım Filmi",
        date: "08.06.2015",
        type: "Tanıtım"
    },
    {
        id: 6,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/cvc-2-300x233.jpg",
        videoUrl: "https://www.youtube.com/watch?v=qX95lNgdRJI",
        title: "Hacettepe Üniversitesi Tanıtım Filmi",
        date: "16.06.2015",
        type: "Tanıtım"
    },
    {
        id: 7,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/cvc-2-300x233.jpg",
        videoUrl: "https://www.youtube.com/watch?v=DsEg0iSR1Zs",
        title: "Prof. Dr. Aziz Sancar'ın Fakültemizi Ziyareti (17.05.2016)",
        date: "23.05.2016",
        type: "Ziyaret"
    },
    {
        id: 8,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/mezuniyet%20foto.jpg",
        videoUrl: "https://www.youtube.com/watch?v=pyEETMoGMjw",
        title: "2019 Yılı Mezuniyet Töreni Bölüm-1",
        date: "28.08.2019",
        type: "Mezuniyet"
    },
    {
        id: 9,
        thumbnail: "https://fs.hacettepe.edu.tr/fenyeni/resimler/mezuniyet%20foto.jpg",
        videoUrl: "https://www.youtube.com/watch?v=Y4K1ppCUK50",
        title: "2019 Yılı Mezuniyet Töreni Bölüm-2",
        date: "28.08.2019",
        type: "Mezuniyet"
    }
];

const VideoCard = ({ video, onVideoClick }) => {
    const [imageError, setImageError] = useState(false);
    const placeholderImage = "https://via.placeholder.com/400x225/1f2937/ffffff?text=Video+Thumbnail";

    const getTypeColor = (type) => {
        switch (type) {
            case 'Mezuniyet': return 'bg-blue-500';
            case 'Tanıtım': return 'bg-green-500';
            case 'Ziyaret': return 'bg-purple-500';
            default: return 'bg-gray-500';
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
        >
            {/* Video Thumbnail */}
            <div className="relative aspect-video overflow-hidden">
                <img
                    src={imageError ? placeholderImage : video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={() => setImageError(true)}
                />
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                        <Play className="w-8 h-8 text-red-600 fill-current" />
                    </div>
                </div>

                {/* Video Type Badge */}
                <div className={`absolute top-3 left-3 ${getTypeColor(video.type)} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                    {video.type}
                </div>

                {/* Duration Badge */}
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    <Youtube className="w-3 h-3 inline mr-1" />
                    YouTube
                </div>
            </div>

            {/* Video Info */}
            <div className="p-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300">
                    {video.title}
                </h3>
                
                <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {video.date}
                    </div>
                    
                    <button
                        onClick={() => onVideoClick(video)}
                        className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
                    >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        İzle
                    </button>
                </div>
            </div>

            {/* Hover Effect Border */}
            <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-500 rounded-xl transition-colors duration-300 pointer-events-none"></div>
        </motion.div>
    );
};

const VideoModal = ({ video, isOpen, onClose }) => {
    if (!isOpen || !video) return null;

    const getYouTubeEmbedUrl = (url) => {
        const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
        return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url;
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Modal Header */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800">{video.title}</h2>
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700 transition-colors duration-300"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Video Player */}
                    <div className="aspect-video">
                        <iframe
                            src={getYouTubeEmbedUrl(video.videoUrl)}
                            title={video.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>

                    {/* Modal Footer */}
                    <div className="p-6 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm text-gray-600">
                                <Calendar className="w-4 h-4 mr-2" />
                                {video.date}
                            </div>
                            <a
                                href={video.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
                            >
                                <Youtube className="w-4 h-4 mr-1" />
                                YouTube'da Aç
                            </a>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const Videogaleri = () => {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterType, setFilterType] = useState('Tümü');

    const videoTypes = ['Tümü', 'Mezuniyet', 'Tanıtım', 'Ziyaret'];

    const filteredVideos = filterType === 'Tümü' 
        ? videoData 
        : videoData.filter(video => video.type === filterType);

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedVideo(null);
    };

    return (
        <PageTemplate 
            title="Video Galeri" 
            subtitle="Fakültemizin önemli anlarını ve etkinliklerini video formatında izleyin"
        >
            {/* Filter Section */}
            <div className="mb-8">
                <div className="flex flex-wrap gap-3">
                    {videoTypes.map((type) => (
                        <button
                            key={type}
                            onClick={() => setFilterType(type)}
                            className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                                filterType === type
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* Video Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVideos.map((video) => (
                    <VideoCard 
                        key={video.id} 
                        video={video} 
                        onVideoClick={handleVideoClick}
                    />
                ))}
            </div>

            {/* Empty State */}
            {filteredVideos.length === 0 && (
                <div className="text-center py-20">
                    <div className="text-gray-400 mb-4">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p className="text-gray-600 text-lg">Bu kategoride video bulunamadı.</p>
                </div>
            )}

            {/* Video Modal */}
            <VideoModal 
                video={selectedVideo}
                isOpen={isModalOpen}
                onClose={closeModal}
            />
        </PageTemplate>
    );
};

export default Videogaleri; 